<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';
import OpusRecorder from 'opus-recorder/dist/recorder.min.js';
import encoderWorkerUrl from 'opus-recorder/dist/encoderWorker.min.js?url';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const opusRecorder = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);

const isOggOpus = () => props.audioRecordFormat === 'audio/ogg;codecs=opus';

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    if (isOggOpus()) {
      // Load webm blob for waveform playback visualization only;
      // actual OGG/Opus file is produced by opusRecorder
      const audioUrl = URL.createObjectURL(blob);
      wavesurfer.value.load(audioUrl);
      hasRecording.value = true;
      return;
    }

    const audioUrl = URL.createObjectURL(blob);
    const audioBlob = await convertAudio(blob, props.audioRecordFormat);
    const fileName = `${getUuid()}.mp3`;
    const file = new File([audioBlob], fileName, {
      type: props.audioRecordFormat,
    });
    wavesurfer.value.load(audioUrl);
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    hasRecording.value = true;
    isRecording.value = false;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const initOpusRecorder = () => {
  opusRecorder.value = new OpusRecorder({
    encoderPath: encoderWorkerUrl,
    streamPages: false,
    numberOfChannels: 1,
    encoderSampleRate: 48000,
  });

  opusRecorder.value.ondataavailable = typedArray => {
    const blob = new Blob([typedArray], { type: 'audio/ogg;codecs=opus' });
    const fileName = `${getUuid()}.ogg`;
    const file = new File([blob], fileName, { type: 'audio/ogg;codecs=opus' });
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    isRecording.value = false;
  };
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    if (isOggOpus() && opusRecorder.value) {
      opusRecorder.value.stop();
    }
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  if (isOggOpus() && opusRecorder.value) {
    opusRecorder.value.start();
  }
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  // eslint-disable-next-line no-console
  console.log('[AudioRecorder] format:', props.audioRecordFormat, '| isOggOpus:', isOggOpus());
  initWaveSurfer();
  if (isOggOpus()) {
    initOpusRecorder();
  }
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
  if (opusRecorder.value) {
    opusRecorder.value.close();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
